// The workspace's context keys and the switch that refuses unsigned context
// (context_keys.rs).
//   GET/PATCH /v1/workspaces/{ws}/security
//   POST /v1/workspaces/{ws}/context-keys, PATCH/DELETE .../{keyId}
//
// One query backs the whole Security page: the switch, the key list and the
// count of unsigned context all move together, so splitting them would only
// create ways for the page to disagree with itself.
//
// Every mutation returns its invalidation, so it settles only once the page's
// query has refetched: the card that leaves edit mode (or the confirmation
// that closes) already shows the new state, never the old one for a beat.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "./http";
import { securityKeys } from "./keys";
import type { ContextKey, SecuritySettings } from "./types";

export interface CreateContextKey {
    label: string;
    algorithm: string;
    publicKey: string;
}

export function useSecurity(workspaceId: string) {
    return useQuery({
        queryKey: securityKeys.detail(workspaceId),
        queryFn: () => http.get<SecuritySettings>(`/v1/workspaces/${workspaceId}/security`),
        enabled: Boolean(workspaceId),
    });
}

export function useCreateContextKey(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateContextKey) =>
            http.post<ContextKey>(`/v1/workspaces/${workspaceId}/context-keys`, { json: body }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: securityKeys.detail(workspaceId) }),
    });
}

export function usePatchContextKey(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ keyId, label }: { keyId: string; label: string }) =>
            http.patch<ContextKey>(`/v1/workspaces/${workspaceId}/context-keys/${keyId}`, { json: { label } }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: securityKeys.detail(workspaceId) }),
    });
}

export function useDeleteContextKey(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        // The id arrives at mutate time so one hook serves every row.
        mutationFn: (keyId: string) => http.delete(`/v1/workspaces/${workspaceId}/context-keys/${keyId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: securityKeys.detail(workspaceId) }),
    });
}

export function usePatchSecurity(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (requireSignedContext: boolean) =>
            http.patch(`/v1/workspaces/${workspaceId}/security`, { json: { requireSignedContext } }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: securityKeys.detail(workspaceId) }),
    });
}
